import React, { useEffect, useState } from 'react';
import { CalendarClock, Check, ChevronDown, ChevronUp, Loader2, X } from 'lucide-react';
import { getPortalTenantN8nExtras } from '../../../constants';
import { isDemoFristMotorPreviewMode } from './demoFristSeed';
import { previewPortalFrist } from './portalFristen';
import { useFristApprovalOptional } from './FristApprovalContext';
import type { ApprovalFristPayload, FristApprovalCardStatus } from './fristApprovalTypes';

function formatDeDate(iso: string): string {
  const d = iso.trim();
  if (!/^\d{4}-\d{2}-\d{2}/.test(d)) return d;
  const [y, m, day] = d.slice(0, 10).split('-');
  return `${day}.${m}.${y}`;
}

const STATUS_LABELS: Record<FristApprovalCardStatus, string> = {
  pending: 'Bestätigung ausstehend',
  approved: 'Bestätigt — in Akte gespeichert',
  rejected: 'Abgelehnt',
  self_answered: 'Selbst beantwortet',
  revoked: 'Widerrufen',
  error: 'Fehler',
};

export interface FristApprovalCardProps {
  payload: ApprovalFristPayload;
  /** `chat` = unter Bot-Nachricht; `mirror` = Dokument-Spiegel. */
  variant?: 'chat' | 'mirror';
  className?: string;
}

export const FristApprovalCard: React.FC<FristApprovalCardProps> = ({
  payload,
  variant = 'chat',
  className = '',
}) => {
  const ctx = useFristApprovalOptional();
  const [busy, setBusy] = useState<FristApprovalCardStatus | null>(null);
  const [traceOpen, setTraceOpen] = useState(false);
  const [displayPayload, setDisplayPayload] = useState(payload);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    setDisplayPayload(payload);
    setPreviewError(null);
  }, [payload]);

  useEffect(() => {
    if (!isDemoFristMotorPreviewMode() || !ctx) return;
    let cancelled = false;

    const runPreview = async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      const tenantExtras = getPortalTenantN8nExtras(ctx.customerStatus, ctx.customerEmail);
      const result = await previewPortalFrist({
        customerId: ctx.customerId,
        card_id: payload.card_id,
        approval_kind: payload.approval_kind,
        frist_typ_vorschlag: payload.frist_typ_vorschlag,
        start_datum: payload.start_datum,
        regel_id: payload.regel_id,
        akte_nc_path: payload.akte_nc_path,
        quell_dokument_nc_path: payload.quell_dokument_nc_path,
        ...tenantExtras,
        customerStatus: ctx.customerStatus?.trim() || undefined,
      });

      if (cancelled) return;
      setPreviewLoading(false);

      if (!result.ok || !result.ende_datum) {
        setPreviewError(result.error ?? 'Motor-Vorschau fehlgeschlagen');
        return;
      }

      setDisplayPayload((prev) => ({
        ...prev,
        ende_vorschau: result.ende_datum ?? prev.ende_vorschau,
        kalender_trace: result.kalender_trace ?? prev.kalender_trace,
        motor_audit_id: result.motor_audit_id ?? prev.motor_audit_id,
        regel_id: result.regel_id ?? prev.regel_id,
      }));
    };

    void runPreview();
    return () => {
      cancelled = true;
    };
  }, [
    ctx,
    payload.approval_kind,
    payload.akte_nc_path,
    payload.card_id,
    payload.frist_typ_vorschlag,
    payload.quell_dokument_nc_path,
    payload.regel_id,
    payload.start_datum,
  ]);

  useEffect(() => {
    ctx?.registerCard(displayPayload);
    // registerCard ist stabil; kein ganzes ctx in deps (verhindert Re-Register-Loop).
  }, [ctx?.registerCard, displayPayload]);

  const cardState = ctx?.getCard(displayPayload.card_id);
  const status: FristApprovalCardStatus = cardState?.status ?? 'pending';
  const isPending = status === 'pending';
  const isApproved = status === 'approved';

  const handleDecision = async (decision: 'approved' | 'rejected' | 'self_answered') => {
    if (!ctx || !isPending) return;
    setBusy(decision === 'approved' ? 'approved' : decision === 'rejected' ? 'rejected' : 'self_answered');
    try {
      await ctx.resolveCard(displayPayload.card_id, decision);
    } finally {
      setBusy(null);
    }
  };

  const handleRevoke = async () => {
    if (!ctx || !isApproved || !cardState?.pbRecordId) return;
    setBusy('revoked');
    try {
      await ctx.revokeCard(displayPayload.card_id);
    } finally {
      setBusy(null);
    }
  };

  const shellClass =
    variant === 'mirror'
      ? 'rounded-lg border border-amber-500/25 bg-amber-500/[0.06] p-3 shadow-sm'
      : 'rounded-xl border border-amber-500/30 bg-amber-500/[0.07] p-3 mt-2';

  return (
    <div
      className={`${shellClass} ${className}`}
      data-card-id={displayPayload.card_id}
      data-approval-variant={variant}
      role="group"
      aria-label="Frist-Bestätigung"
    >
      <div className="flex items-start gap-2">
        <CalendarClock className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-300/90 m-0">
            {variant === 'mirror' ? 'Frist-Spiegel' : 'Frist bestätigen'}
          </p>
          <p className="text-sm font-medium text-slate-100 m-0 mt-0.5">
            {displayPayload.frist_typ_vorschlag}
          </p>
          {displayPayload.begruendung_kurz ? (
            <p className="text-xs text-slate-400 m-0 mt-1">{displayPayload.begruendung_kurz}</p>
          ) : null}
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
        <div>
          <dt className="text-slate-500">Start</dt>
          <dd className="text-slate-200 m-0 font-medium">{formatDeDate(displayPayload.start_datum)}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Ende (Vorschau)</dt>
          <dd className="text-slate-200 m-0 font-medium inline-flex items-center gap-1">
            {previewLoading ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin text-amber-400" aria-hidden />
                <span className="text-slate-400">Motor…</span>
              </>
            ) : (
              formatDeDate(displayPayload.ende_vorschau)
            )}
          </dd>
        </div>
      </dl>

      {previewError ? (
        <p className="text-[11px] text-amber-300/90 m-0 mt-1">{previewError}</p>
      ) : null}

      {displayPayload.kalender_trace ? (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setTraceOpen((v) => !v)}
            className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-200 transition-colors"
            aria-expanded={traceOpen}
          >
            {traceOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Kalender-Nachvollziehbarkeit
          </button>
          {traceOpen ? (
            <p className="text-[11px] leading-relaxed text-slate-400 m-0 mt-1 whitespace-pre-wrap">
              {displayPayload.kalender_trace}
            </p>
          ) : null}
        </div>
      ) : null}

      <p className="text-[10px] text-slate-500 m-0 mt-2">{STATUS_LABELS[status]}</p>
      {cardState?.errorMessage ? (
        <p className="text-[11px] text-red-300 m-0 mt-1">{cardState.errorMessage}</p>
      ) : null}

      {isPending && ctx ? (
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            type="button"
            disabled={Boolean(busy)}
            onClick={() => void handleDecision('approved')}
            className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors"
          >
            {busy === 'approved' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Ja
          </button>
          <button
            type="button"
            disabled={Boolean(busy)}
            onClick={() => void handleDecision('rejected')}
            className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-50 px-2.5 py-1.5 text-xs font-medium text-slate-200 transition-colors"
          >
            {busy === 'rejected' ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
            Nein
          </button>
          <button
            type="button"
            disabled={Boolean(busy)}
            onClick={() => void handleDecision('self_answered')}
            className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-50 px-2.5 py-1.5 text-xs font-medium text-slate-300 transition-colors"
          >
            Selbst beantworten
          </button>
        </div>
      ) : null}

      {isApproved && cardState?.pbRecordId && ctx ? (
        <div className="mt-2">
          <button
            type="button"
            disabled={Boolean(busy)}
            onClick={() => void handleRevoke()}
            className="text-[11px] text-slate-500 hover:text-amber-300 underline-offset-2 hover:underline disabled:opacity-50"
          >
            {busy === 'revoked' ? 'Widerruf…' : 'Frist widerrufen'}
          </button>
        </div>
      ) : null}
    </div>
  );
};

/** Registriert Payload im Context (für InlineChat beim Bot-Eingang). */
export function useRegisterApprovalFrist(payload: ApprovalFristPayload | undefined): void {
  const ctx = useFristApprovalOptional();
  useEffect(() => {
    if (payload && ctx) ctx.registerCard(payload);
  }, [ctx?.registerCard, payload]);
}
